import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import {
  Search, Bot, Mic, Code, ArrowRight, CheckCircle2,
  TrendingUp, Globe, MessageSquare, Eye, Shield, Sparkles
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const COMPARISON = [
  { aspect: "What It Stands For", seo: "Search Engine Optimization", aeo: "Answer Engine Optimization", geo: "Generative Engine Optimization" },
  { aspect: "Primary Goal", seo: "Rank web pages in organic search results", aeo: "Become the direct answer to questions", geo: "Get cited by AI-generated answers" },
  { aspect: "Target Platform", seo: "Google, Bing (blue links)", aeo: "Featured snippets, PAA, Siri, Alexa", geo: "ChatGPT, Perplexity, AI Overviews, Copilot" },
  { aspect: "Key Metric", seo: "Rankings, organic traffic, clicks", aeo: "Featured snippet wins, voice answer rate", geo: "AI citation frequency & sentiment" },
  { aspect: "Content Strategy", seo: "Long-form keyword-optimized pages", aeo: "Concise Q&A pairs, structured definitions", geo: "Entity-rich factual prose, citation-optimized" },
  { aspect: "Schema Required", seo: "Article, Breadcrumb, Organization", aeo: "FAQ, HowTo, Speakable, Q&A", geo: "All of the above + Service, SpeakableSpec" },
  { aspect: "Link Building", seo: "Domain authority & PageRank", aeo: "Not a primary factor", geo: "Third-party mentions for LLM training data" },
  { aspect: "Timeline to Results", seo: "3-6 months", aeo: "2-6 weeks (snippets), 1-3 months (voice)", geo: "2-4 months (AI citations)" },
  { aspect: "Cost Range", seo: "$1,000 - $10,000/mo", aeo: "$500 - $3,000/mo (addon)", geo: "$1,500 - $5,000/mo" },
  { aspect: "Best For", seo: "All businesses (foundation)", aeo: "FAQ-heavy industries, voice-first", geo: "Businesses wanting AI recommendations" },
];

const WHEN_TO_USE = [
  { title: "Use SEO When...", color: "text-green-400", border: "border-green-500/20", bg: "bg-green-500/5", items: ["You need a strong organic foundation", "You're starting from scratch online", "You want sustained, compounding traffic", "You're in a competitive SERP landscape"] },
  { title: "Use AEO When...", color: "text-emerald-400", border: "border-emerald-500/20", bg: "bg-emerald-500/5", items: ["Your audience asks specific questions", "Voice search is relevant to your industry", "You want to capture featured snippets", "You have FAQ-heavy content to leverage"] },
  { title: "Use GEO When...", color: "text-purple-400", border: "border-purple-500/20", bg: "bg-purple-500/5", items: ["You want AI to recommend your brand", "Competitors are getting AI citations", "Your audience uses ChatGPT/Perplexity", "You want to future-proof your visibility"] },
];

const FAQS = [
  { q: "Should I choose one over the other?", a: "No — they're synergistic layers, not competitors. Think of it as a stack: SEO is the foundation (organic rankings), AEO is the answer layer (snippets & voice), and GEO is the AI layer (ChatGPT, Perplexity, AI Overviews). The strongest digital presence uses all three." },
  { q: "What's the best order to implement them?", a: "Start with SEO (3-6 months to build the foundation), then layer in AEO (schema + snippet optimization), then add GEO (entity building + AI citation strategy). Most businesses should start all three simultaneously but expect results in this order." },
  { q: "Can one agency do all three?", a: "Yes — in fact, it's better to have one agency handle all three because they share infrastructure. Your SEO content strategy feeds AEO snippet opportunities, which feeds GEO citation opportunities. We handle all three at Digital Media Ninja." },
  { q: "Is GEO just a fad?", a: "No. AI search is accelerating, not slowing. OpenAI, Google, Microsoft, and Apple are all investing billions in AI-powered search. By 2027, an estimated 50%+ of search queries will be answered by AI. GEO is as fundamental as SEO was in 2010." },
  { q: "How do I know which I need most?", a: "Take our free AI Visibility Audit. We'll assess your current performance across all three layers — organic rankings (SEO), featured snippet presence (AEO), and AI citation frequency (GEO) — and recommend where to invest first." },
];

const AEOvsGEOvsSEOPage = () => (
  <div className="min-h-screen pt-28 pb-20" data-testid="aeo-vs-geo-vs-seo-page">
    <Helmet>
      <title>AEO vs GEO vs SEO: Complete Comparison Guide (2026) | Digital Media Ninja</title>
      <meta name="description" content="AEO vs GEO vs SEO — what's the difference? Complete comparison of Answer Engine Optimization, Generative Engine Optimization, and traditional SEO. Which do you need?" />
      <link rel="canonical" href="https://digitalmedianinja.com/aeo-vs-geo-vs-seo/" />
    </Helmet>

    <article className="container-custom max-w-5xl mx-auto px-4" data-speakable="true">
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#2ABFBF]/10 border border-[#2ABFBF]/20 text-[#2ABFBF] text-sm font-medium mb-6">
          <Sparkles className="w-4 h-4" /> Comparison Guide
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6 leading-tight">AEO vs GEO vs SEO</h1>
        <p className="text-lg text-gray-400 max-w-3xl mx-auto" data-speakable="true">
          Three optimization disciplines, one goal: getting your brand found by the right customers. Here's how SEO, AEO, and GEO differ — and why you probably need all three.
        </p>
      </div>

      {/* Quick Summary */}
      <section className="mb-12">
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-green-500/5 border border-green-500/20 rounded-xl p-5">
            <Search className="w-8 h-8 text-green-400 mb-3" />
            <h3 className="text-white font-bold mb-2">SEO</h3>
            <p className="text-gray-400 text-sm">Rank in Google's blue links. The foundation of online visibility since 1998.</p>
          </div>
          <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-5">
            <Mic className="w-8 h-8 text-emerald-400 mb-3" />
            <h3 className="text-white font-bold mb-2">AEO</h3>
            <p className="text-gray-400 text-sm">Become the direct answer. Featured snippets, PAA, voice search, and answer boxes.</p>
          </div>
          <div className="bg-purple-500/5 border border-purple-500/20 rounded-xl p-5">
            <Bot className="w-8 h-8 text-purple-400 mb-3" />
            <h3 className="text-white font-bold mb-2">GEO</h3>
            <p className="text-gray-400 text-sm">Get cited by AI. ChatGPT, Perplexity, Google AI Overviews, Copilot.</p>
          </div>
        </div>
      </section>

      {/* Detailed Comparison Table */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold text-white mb-6">Side-by-Side Comparison</h2>
        <div className="bg-white/5 border border-white/10 rounded-xl overflow-x-auto" data-testid="comparison-table">
          <div className="min-w-[700px]">
            <div className="grid grid-cols-4 bg-white/5 px-5 py-3 border-b border-white/10">
              <span className="text-gray-400 text-sm font-medium">Aspect</span>
              <span className="text-green-400 text-sm font-medium">SEO</span>
              <span className="text-emerald-400 text-sm font-medium">AEO</span>
              <span className="text-purple-400 text-sm font-medium">GEO</span>
            </div>
            {COMPARISON.map((row, i) => (
              <div key={i} className="grid grid-cols-4 px-5 py-3 border-b border-white/5 last:border-0">
                <span className="text-white text-sm font-medium">{row.aspect}</span>
                <span className="text-gray-400 text-sm">{row.seo}</span>
                <span className="text-gray-300 text-sm">{row.aeo}</span>
                <span className="text-gray-300 text-sm">{row.geo}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* When to Use */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold text-white mb-6">When to Use Each</h2>
        <div className="grid md:grid-cols-3 gap-4">
          {WHEN_TO_USE.map((section, i) => (
            <div key={i} className={`${section.bg} border ${section.border} rounded-xl p-5`}>
              <h3 className={`${section.color} font-bold mb-3`}>{section.title}</h3>
              <ul className="space-y-2">
                {section.items.map((item, j) => (
                  <li key={j} className="flex items-start gap-2 text-gray-300 text-sm">
                    <CheckCircle2 className={`w-4 h-4 ${section.color} shrink-0 mt-0.5`} />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* The Stack */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold text-white mb-6">The SEO + AEO + GEO Stack</h2>
        <div className="bg-gradient-to-b from-purple-500/10 via-emerald-500/10 to-green-500/10 border border-white/10 rounded-xl p-6">
          <div className="space-y-3">
            <div className="bg-purple-500/20 border border-purple-500/30 rounded-lg p-4 text-center">
              <Bot className="w-6 h-6 text-purple-400 mx-auto mb-2" />
              <h3 className="text-white font-bold">GEO Layer</h3>
              <p className="text-gray-400 text-xs">AI citations, entity authority, RAG optimization</p>
            </div>
            <div className="bg-emerald-500/20 border border-emerald-500/30 rounded-lg p-4 text-center">
              <Mic className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
              <h3 className="text-white font-bold">AEO Layer</h3>
              <p className="text-gray-400 text-xs">Featured snippets, PAA, voice search, FAQ schema</p>
            </div>
            <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-4 text-center">
              <Search className="w-6 h-6 text-green-400 mx-auto mb-2" />
              <h3 className="text-white font-bold">SEO Foundation</h3>
              <p className="text-gray-400 text-xs">Technical SEO, content authority, backlinks, organic rankings</p>
            </div>
          </div>
          <p className="text-gray-400 text-sm mt-4 text-center">Each layer builds on the one below it. The strongest visibility uses all three.</p>
        </div>
      </section>

      {/* FAQs */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold text-white mb-6">Frequently Asked Questions</h2>
        <Accordion type="single" collapsible className="space-y-3">
          {FAQS.map((faq, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="bg-white/5 border border-white/10 rounded-xl px-5">
              <AccordionTrigger className="text-white text-left text-sm font-medium hover:no-underline">{faq.q}</AccordionTrigger>
              <AccordionContent className="text-gray-400 text-sm">{faq.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Definition Cross-links */}
      <div className="grid sm:grid-cols-3 gap-4 mb-12">
        <Link to="/what-is-aeo" className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-4 hover:bg-emerald-500/10 transition-colors">
          <h3 className="text-white font-semibold text-sm mb-1">What Is AEO?</h3>
          <p className="text-gray-400 text-xs">Deep dive into Answer Engine Optimization</p>
        </Link>
        <Link to="/what-is-geo" className="bg-purple-500/5 border border-purple-500/20 rounded-xl p-4 hover:bg-purple-500/10 transition-colors">
          <h3 className="text-white font-semibold text-sm mb-1">What Is GEO?</h3>
          <p className="text-gray-400 text-xs">Deep dive into Generative Engine Optimization</p>
        </Link>
        <Link to="/what-is-llmo" className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-4 hover:bg-blue-500/10 transition-colors">
          <h3 className="text-white font-semibold text-sm mb-1">What Is LLMO?</h3>
          <p className="text-gray-400 text-xs">Deep dive into LLM Optimization</p>
        </Link>
      </div>

      <div className="bg-[#111] border border-white/10 rounded-2xl p-8 text-center">
        <h2 className="text-2xl font-bold text-white mb-3">Not Sure Which You Need?</h2>
        <p className="text-gray-400 mb-6 max-w-xl mx-auto">Our free AI Visibility Audit checks all three layers — SEO, AEO, and GEO — and tells you exactly where to invest first.</p>
        <Link to="/free-ai-visibility-audit">
          <Button className="bg-[#2ABFBF] hover:bg-[#1E9999] text-black font-semibold px-8" data-testid="comparison-bottom-cta">Get Your Free AI Visibility Audit</Button>
        </Link>
      </div>
    </article>
  </div>
);

export default AEOvsGEOvsSEOPage;
